import { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { BaseAPIClient } from '@mi-front/core';

let apiClient: BaseAPIClient | null = null;
let configuredApiRoot: string | undefined;

/** Registers the shared axios client used by generated Kubb public API calls. */
export function setApiPublicV2Client(client: BaseAPIClient, options: { apiRoot?: string } = {}) {
  apiClient = client;
  configuredApiRoot = options.apiRoot;
}

/** Returns the axios client registered for api-public-v2 generated hooks and functions. */
export function getApiPublicV2Client() {
  if (!apiClient) {
    throw new Error(
      'ApiPublicV2 client not set. Call setApiPublicV2Client() during app bootstrap.',
    );
  }

  return apiClient;
}

/**
 * Resolves the public API base URL for Vite (app) and Next.js (social).
 * Prefers explicit env vars, then falls back by environment name.
 */
function resolveDefaultApiRoot(): string {
  const viteEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
      : undefined;

  const fromVite = viteEnv?.VITE_API_PUBLIC_V2_ROOT ?? viteEnv?.VITE_API_V2_ROOT;
  if (fromVite) {
    return fromVite;
  }

  const fromProcess =
    typeof process !== 'undefined'
      ? process.env.NEXT_PUBLIC_API_PUBLIC_V2_ROOT ||
        process.env.NEXT_PUBLIC_API_V2_ROOT ||
        process.env.NEXT_PUBLIC_API_ROOT
      : undefined;
  if (fromProcess) {
    return fromProcess;
  }

  const envName =
    viteEnv?.VITE_ENV ??
    (typeof process !== 'undefined'
      ? process.env.NEXT_PUBLIC_APP_ENV || process.env.APP_ENV || process.env.VITE_ENV
      : undefined) ??
    '';

  return envName.startsWith('prod') ? 'https://api.mediainbox.io' : 'https://api-dev.mediainbox.io';
}

const defaultApiRoot = resolveDefaultApiRoot();

/** Subset of AxiosRequestConfig expected by Kubb-generated clients. */
export type RequestConfig<TVariables = unknown> = {
  baseURL?: string;
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS' | 'HEAD';
  url?: string;
  params?: unknown;
  data?: TVariables | FormData;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  signal?: AbortSignal;
  headers?: AxiosRequestConfig['headers'];
};

/** Subset of AxiosResponse expected by Kubb-generated clients. */
export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
  headers: AxiosResponse['headers'];
};

/** Error type used in Kubb-generated client and hook signatures. */
export type ResponseErrorConfig<TError = unknown> = AxiosError<TError>;

/**
 * Axios wrapper consumed by Kubb-generated public API clients.
 * Uses VITE_API_PUBLIC_V2_ROOT / NEXT_PUBLIC_API_* when set, otherwise falls back by env.
 */
export const axiosClient = async <TData, TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<AxiosResponse<TData>> => {
  return getApiPublicV2Client()
    .request<TData>(config.method as Method, config.url as string, {
      params: config.params,
      data: config.data,
      responseType: config.responseType,
      signal: config.signal,
      headers: config.headers,
      baseURL: config.baseURL ?? configuredApiRoot ?? defaultApiRoot,
    })
    .catch((e: AxiosError<TError>) => {
      throw e;
    });
};

axiosClient.generateUrl = (url: string) =>
  getApiPublicV2Client().generateUrl(url, { secure: true });

/** Client shape expected by Kubb-generated functions (`config.client` override). */
export type Client = typeof axiosClient;

export default axiosClient;
